import { Classes, Icon, IconName, Intent } from '@clarion-blueprint-shim';
import { ReactNode } from 'react';
import { Button, PressEvent } from 'react-aria-components';
import { tv } from 'tailwind-variants';

export interface ClarionTagProps {
  children?: ReactNode;
  active?: boolean;
  fill?: boolean;
  icon?: IconName;
  intent?: Intent;
  interactive?: boolean;
  onPress?: (e: PressEvent) => void;
  large?: boolean;
  minimal?: boolean;
  onRemove?: (e: PressEvent) => void;
  rightIcon?: IconName;
  round?: boolean;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
}

const tagStyles = tv({
  base: Classes.TAG,
  variants: {
    active: {
      true: Classes.ACTIVE,
    },
    fill: {
      true: Classes.FILL,
    },
    intent: {
      none: '',
      primary: Classes.INTENT_PRIMARY,
      success: Classes.INTENT_SUCCESS,
      warning: Classes.INTENT_WARNING,
      danger: Classes.INTENT_DANGER,
    },
    interactive: {
      true: Classes.INTERACTIVE,
    },
    large: {
      true: Classes.LARGE,
    },
    minimal: {
      true: Classes.MINIMAL,
    },
    round: {
      true: Classes.ROUND,
    },
  },
  defaultVariants: {
    intent: 'none',
  },
});

export function ClarionTag({
  children,
  active,
  fill,
  icon,
  intent = 'none',
  interactive,
  onPress,
  large,
  minimal,
  onRemove,
  rightIcon,
  round,
  title,
  className,
  style,
}: ClarionTagProps) {
  const tagClass = tagStyles({
    active,
    fill,
    intent,
    interactive,
    large,
    minimal,
    round,
    className,
  });

  const content = (
    <>
      {icon && <Icon icon={icon} />}
      {children && <span>{children}</span>}
      {rightIcon && <Icon icon={rightIcon} />}
      {onRemove && (
        <Button className={Classes.TAG_REMOVE} onPress={onRemove} aria-label="Remove tag">
          <Icon icon="small-cross" />
        </Button>
      )}
    </>
  );

  if (interactive && !onRemove) {
    return (
      <Button className={tagClass} onPress={onPress} style={style}>
        {content}
      </Button>
    );
  }

  return (
    <span className={tagClass} style={style} title={title}>
      {content}
    </span>
  );
}
